import { Box, HBox, VBox } from "@ui/box/Box"
import { Typo } from "@ui/text/Typo"
import { type ReactNode } from "react"
import { toast } from "react-toastify"

// One visual language for every rebuilt page and modal. New screens compose
// these blocks instead of hand-styling boxes, so they stay consistent.
export const DS = {
	ink: "#0F172A",
	text: "#334155",
	muted: "#64748B",
	faint: "#94A3B8",
	line: "#E8EBF3",
	soft: "#F6F7FB",
	surface: "#FFFFFF",
	// Brand tokens are CSS variables so a workspace (e.g. private) can re-skin
	// every Kit block by setting them on its root. Defaults: corporate navy.
	accent: "var(--ds-accent, #2B5C8A)",
	accentSoft: "var(--ds-accent-soft, #E8F0F8)",
	accentInk: "var(--ds-accent-ink, #1D3F61)",
	accentLine: "var(--ds-accent-line, #C9D9E9)",
	gradient: "var(--ds-gradient, linear-gradient(180deg, #2C537A 0%, #1F3D5D 100%))",
	heroGradient: "var(--ds-hero-gradient, linear-gradient(135deg, #34618C 0%, #22446A 55%, #182F4A 100%))",
	stageBg: "var(--ds-stage-bg, radial-gradient(90% 120% at 0% 0%, #E2EDF8 0%, rgba(233,231,255,0) 60%), radial-gradient(70% 90% at 100% 100%, #FBE9DC 0%, rgba(243,232,255,0) 60%), #FBFBFF)",
	cardFace: "var(--ds-card-face, linear-gradient(135deg, #1B2D44 0%, #0E1826 100%))",
	radius: "16px",
	shadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 16px rgba(15, 23, 42, 0.04)",
	mobile: "@media (max-width: 620px)"
} as const

const money = { fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" } as const

export const Avatar = ({ name, size = 48 }: { name?: string; size?: number }) => (
	<Box
		aria-hidden
		sx={{
			width: `${size}px`,
			height: `${size}px`,
			minWidth: `${size}px`,
			borderRadius: `${Math.round(size * 0.3)}px`,
			background: DS.gradient,
			color: "#FFFFFF",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			fontWeight: 700,
			fontSize: `${Math.round(size * 0.4)}px`,
			boxShadow: "0 6px 16px rgba(30, 58, 95, 0.3)"
		}}
	>
		{(name?.trim()[0] ?? "?").toUpperCase()}
	</Box>
)

/** Modal/page header: identity on the left, actions on the right. */
export const EntityHeader = ({ leading, title, meta, badge, actions, stacked }: {
	/** Put actions on their own row under the identity (modals); side by side otherwise (pages). */
	stacked?: boolean
	leading?: ReactNode
	title: ReactNode
	meta?: ReactNode
	badge?: ReactNode
	actions?: ReactNode
}) => (
	<HBox sx={{ alignItems: "center", gap: "16px", flexWrap: "wrap", [DS.mobile]: { alignItems: "flex-start" } }}>
		<HBox sx={{ alignItems: "center", gap: "14px", minWidth: 0, flex: stacked ? "1 1 100%" : "1 1 280px", paddingRight: stacked ? "48px" : 0 }}>
			{leading}
			<VBox gap='4px' sx={{ minWidth: 0 }}>
				<HBox gap='10px' sx={{ alignItems: "center", flexWrap: "wrap" }}>
					<Typo sx={{ fontSize: "20px", fontWeight: 700, color: DS.ink, letterSpacing: "-0.02em", lineHeight: 1.2, overflowWrap: "anywhere" }}>{title}</Typo>
					{badge}
				</HBox>
				{meta && <Typo sx={{ fontSize: "13px", color: DS.muted, lineHeight: 1.45, overflowWrap: "anywhere" }}>{meta}</Typo>}
			</VBox>
		</HBox>
		{actions && (
			<HBox
				gap='8px'
				sx={{ alignItems: "center", flexWrap: "wrap", ...(stacked ? { flex: "1 1 100%", justifyContent: "flex-end", paddingTop: "14px", borderTop: `1px solid ${DS.line}` } : {}) }}
			>
				{actions}
			</HBox>
		)}
	</HBox>
)

/** Underline tabs; keeps aria-pressed so existing tab tests and keyboard use keep working. */
export const UnderlineTabs = <T extends string>({ tabs, active, onChange, label }: {
	tabs: readonly T[]
	active: T
	onChange: (tab: T) => void
	label: string
}) => (
	<HBox role='group' aria-label={label} sx={{ gap: "4px", flexShrink: 0, borderBottom: `1px solid ${DS.line}`, overflowX: "auto" }}>
		{tabs.map(tab => {
			const on = tab === active
			return (
				<Box
					key={tab}
					element='button'
					type='button'
					aria-pressed={on}
					onClick={() => onChange(tab)}
					sx={{
						border: 0,
						background: "transparent",
						fontFamily: "inherit",
						padding: "10px 14px",
						minHeight: "44px",
						cursor: "pointer",
						fontSize: "14px",
						fontWeight: 600,
						textTransform: "capitalize",
						color: on ? DS.ink : DS.muted,
						boxShadow: on ? `inset 0 -2px 0 ${DS.accent}` : "none",
						whiteSpace: "nowrap",
						"&:hover": { color: DS.ink }
					}}
				>
					{tab}
				</Box>
			)
		})}
	</HBox>
)

/** The one number the screen is about, with its primary actions. */
export const HeroStat = ({ label, value, hint, actions }: { label: string; value: ReactNode; hint?: ReactNode; actions?: ReactNode }) => (
	<HBox
		sx={{
			alignItems: "center",
			justifyContent: "space-between",
			gap: "16px",
			flexWrap: "wrap",
			flexShrink: 0,
			padding: "22px 24px",
			borderRadius: "22px",
			border: `1px solid ${DS.line}`,
			background: DS.stageBg,
			color: DS.ink
		}}
	>
		<VBox gap='6px'>
			<Typo sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.muted }}>{label}</Typo>
			<Typo sx={{ fontSize: "40px", fontWeight: 700, color: DS.ink, lineHeight: 1.05, ...money, letterSpacing: "-0.035em", [DS.mobile]: { fontSize: "32px" } }}>{value}</Typo>
			{hint && <Typo sx={{ fontSize: "13px", color: DS.muted, "& strong": { color: DS.text } }}>{hint}</Typo>}
		</VBox>
		{actions && <HBox gap='10px' sx={{ flexWrap: "wrap" }}>{actions}</HBox>}
	</HBox>
)

export const HeroButton = ({ children, onClick, ghost }: { children: ReactNode; onClick: () => void; ghost?: boolean }) => (
	<Box
		element='button'
		type='button'
		onClick={onClick}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: "8px",
			minHeight: "44px",
			padding: "0 18px",
			borderRadius: "12px",
			fontFamily: "inherit",
			fontSize: "14px",
			fontWeight: 600,
			cursor: "pointer",
			border: ghost ? `1px solid ${DS.line}` : 0,
			background: ghost ? DS.surface : DS.gradient,
			color: ghost ? DS.ink : "#FFFFFF",
			boxShadow: ghost ? "none" : "0 6px 16px rgba(30, 58, 95, 0.28)",
			"&:hover": ghost ? { background: DS.soft } : { filter: "brightness(1.06)" },
			"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" }
		}}
	>
		{children}
	</Box>
)

/** Several secondary numbers in one card, split by hairlines instead of separate boxes. */
export const StatGroup = ({ items }: { items: { label: string; value: ReactNode; hint?: ReactNode }[] }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
			borderRadius: DS.radius,
			border: `1px solid ${DS.line}`,
			background: DS.surface,
			boxShadow: DS.shadow,
			[DS.mobile]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
		}}
	>
		{items.map((item, index) => (
			<VBox
				key={item.label}
				gap='6px'
				sx={{
					padding: "16px 18px",
					minWidth: 0,
					borderLeft: index ? `1px solid ${DS.line}` : 0,
					[DS.mobile]: { borderLeft: index % 2 ? `1px solid ${DS.line}` : 0, borderTop: index > 1 ? `1px solid ${DS.line}` : 0 }
				}}
			>
				<Typo sx={{ fontSize: "12px", fontWeight: 500, color: DS.muted }}>{item.label}</Typo>
				<Typo sx={{ fontSize: "20px", fontWeight: 700, color: DS.ink, lineHeight: 1.15, ...money }}>{item.value}</Typo>
				{item.hint && <Typo sx={{ fontSize: "12px", color: DS.faint, lineHeight: 1.4 }}>{item.hint}</Typo>}
			</VBox>
		))}
	</Box>
)

export const Section = ({ title, action, children }: { title?: string; action?: ReactNode; children: ReactNode }) => (
	<VBox
		gap='4px'
		sx={{ padding: "6px 18px 8px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.surface, boxShadow: DS.shadow }}
	>
		{(title || action) && (
			<HBox sx={{ alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "12px 0 6px" }}>
				{title && <Typo sx={{ fontSize: "15px", fontWeight: 650, color: DS.ink }}>{title}</Typo>}
				{action}
			</HBox>
		)}
		{children}
	</VBox>
)

/** Label/value row. `copy` adds a copy button that announces itself to screen readers. */
export const DetailRow = ({ label, value, mono, copy }: { label: string; value: ReactNode; mono?: boolean; copy?: string }) => (
	<HBox
		sx={{
			alignItems: "center",
			justifyContent: "space-between",
			gap: "16px",
			minHeight: "48px",
			borderTop: `1px solid ${DS.line}`,
			"&:first-of-type": { borderTop: 0 }
		}}
	>
		<Typo sx={{ fontSize: "14px", color: DS.muted, flexShrink: 0 }}>{label}</Typo>
		<HBox gap='6px' sx={{ alignItems: "center", minWidth: 0, justifyContent: "flex-end" }}>
			{typeof value === "string" || typeof value === "number" ? (
				<Typo sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, textAlign: "right", overflowWrap: "anywhere", fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined }}>
					{value}
				</Typo>
			) : value}
			{copy && (
				<Box
					element='button'
					type='button'
					aria-label={`Copy ${label}`}
					onClick={() => {
						void navigator.clipboard.writeText(copy)
						toast.success(`${label} copied`)
					}}
					sx={{
						order: -1,
						width: "28px",
						height: "28px",
						border: 0,
						borderRadius: "8px",
						background: "transparent",
						color: DS.faint,
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						"&:hover": { background: DS.accentSoft, color: DS.accent }
					}}
				>
					<svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
						<rect x='9' y='9' width='13' height='13' rx='2' />
						<path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
					</svg>
				</Box>
			)}
		</HBox>
	</HBox>
)

/** Header action buttons: primary (filled), secondary (outlined) and quiet danger. */
export const ActionButton = ({ children, onClick, tone = "secondary", disabled, ariaLabel }: {
	children: ReactNode
	onClick?: () => void
	tone?: "primary" | "secondary" | "danger" | "success"
	disabled?: boolean
	ariaLabel?: string
}) => {
	const tones = {
		primary: { background: DS.gradient, color: "#FFFFFF", border: "1px solid transparent", boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)", hover: { filter: "brightness(1.05)" } },
		secondary: { background: DS.surface, color: DS.ink, border: `1px solid ${DS.line}`, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", hover: { background: DS.soft } },
		danger: { background: "transparent", color: "#B91C1C", border: "1px solid transparent", boxShadow: "none", hover: { background: "#FEF2F2" } },
		success: { background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0", boxShadow: "none", hover: { background: "#DFF8ED" } }
	}[tone]
	const { hover, ...base } = tones
	return (
		<Box
			element='button'
			type='button'
			aria-label={ariaLabel}
			disabled={disabled}
			onClick={disabled ? undefined : onClick}
			sx={{
				...base,
				minHeight: "40px",
				padding: "0 16px",
				borderRadius: "12px",
				fontFamily: "inherit",
				fontSize: "14px",
				fontWeight: 600,
				cursor: disabled ? "default" : "pointer",
				opacity: disabled ? 0.55 : 1,
				whiteSpace: "nowrap",
				transition: "background-color .15s ease, filter .15s ease",
				"&:hover:not(:disabled)": hover
			}}
		>
			{children}
		</Box>
	)
}

/** Opening block for form dialogs: what this form does, in one line, with an icon. */
export const FormIntro = ({ icon, title, description }: { icon: ReactNode; title: string; description: string }) => (
	<HBox gap='14px' sx={{ alignItems: "center", padding: "14px 16px", marginBottom: "16px", borderRadius: DS.radius, background: "linear-gradient(135deg, #EEF4FA 0%, #E8F0F8 100%)", border: "1px solid #DCE7F2" }}>
		<Box
			aria-hidden
			sx={{ width: "40px", height: "40px", minWidth: "40px", borderRadius: "12px", background: DS.gradient, color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 14px rgba(30, 58, 95, 0.3)" }}
		>
			{icon}
		</Box>
		<VBox gap='2px' sx={{ minWidth: 0 }}>
			<Typo sx={{ fontSize: "15px", fontWeight: 650, color: DS.ink }}>{title}</Typo>
			<Typo sx={{ fontSize: "13px", color: DS.muted, lineHeight: 1.45 }}>{description}</Typo>
		</VBox>
	</HBox>
)

const glyph = (children: ReactNode) => (
	<svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>{children}</svg>
)

export const Glyph = {
	team: glyph(<><circle cx='9' cy='8' r='3.5' /><path d='M2.5 20a6.5 6.5 0 0 1 13 0' /><path d='M16 4.5a3.5 3.5 0 0 1 0 7' /><path d='M21.5 20a6.5 6.5 0 0 0-4-6' /></>),
	invite: glyph(<><circle cx='10' cy='8' r='4' /><path d='M3 21a7 7 0 0 1 14 0' /><path d='M19 8v6' /><path d='M16 11h6' /></>),
	money: glyph(<><rect x='2' y='6' width='20' height='13' rx='2' /><circle cx='12' cy='12.5' r='2.5' /><path d='M6 10v5M18 10v5' /></>),
	shield: glyph(<path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />),
	edit: glyph(<><path d='M12 20h9' /><path d='M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z' /></>),
	card: glyph(<><rect x='2' y='5' width='20' height='14' rx='2' /><path d='M2 10h20' /></>),
	limit: glyph(<><path d='M12 20a8 8 0 1 1 8-8' /><path d='M12 12l4-4' /><path d='M20 16v4h-4' /></>)
}


/** Light stage that holds the hero object of a screen (a card, a balance) with its key figures. */
export const Stage = ({ children, sx }: { children: ReactNode; sx?: Record<string, unknown> }) => (
	<Box
		sx={{
			display: "grid",
			flexShrink: 0,
			gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
			gap: "28px",
			alignItems: "center",
			padding: "24px",
			borderRadius: "22px",
			border: `1px solid ${DS.line}`,
			background: DS.stageBg,
			[DS.mobile]: { gridTemplateColumns: "minmax(0, 1fr)", gap: "20px", padding: "16px" },
			...sx
		}}
	>
		{children}
	</Box>
)

/** Row of round quick actions, banking-app style. */
export const QuickActions = ({ label, children }: { label: string; children: ReactNode }) => (
	<Box role='group' aria-label={label} sx={{ display: "grid", flexShrink: 0, gridTemplateColumns: "repeat(auto-fill, minmax(92px, 116px))", gap: "8px", padding: "14px 12px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.surface, boxShadow: DS.shadow }}>
		{children}
	</Box>
)

export const StageButton = ({ children, onClick, disabled, ghost }: { children: ReactNode; onClick: () => void; disabled?: boolean; ghost?: boolean }) => (
	<Box
		element='button'
		type='button'
		disabled={disabled}
		onClick={disabled ? undefined : onClick}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			gap: "8px",
			minHeight: "44px",
			padding: "0 18px",
			borderRadius: "12px",
			fontFamily: "inherit",
			fontSize: "14px",
			fontWeight: 600,
			cursor: disabled ? "default" : "pointer",
			opacity: disabled ? 0.55 : 1,
			border: ghost ? `1px solid ${DS.line}` : 0,
			background: ghost ? DS.surface : DS.gradient,
			color: ghost ? DS.ink : "#FFFFFF",
			boxShadow: ghost ? "none" : "0 6px 16px rgba(30, 58, 95, 0.28)",
			"&:hover:not(:disabled)": { background: ghost ? DS.soft : DS.gradient, filter: ghost ? "none" : "brightness(1.06)" },
			"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" }
		}}
	>
		{children}
	</Box>
)

export const QuickAction = ({ title, icon, onClick, disabled }: { title: string; icon: ReactNode; onClick: () => void; disabled?: boolean }) => (
	<Box
		element='button'
		type='button'
		disabled={disabled}
		onClick={disabled ? undefined : onClick}
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: "8px",
			minWidth: 0,
			padding: "6px 4px",
			border: 0,
			borderRadius: "14px",
			background: "transparent",
			color: DS.ink,
			fontFamily: "inherit",
			fontSize: "13px",
			fontWeight: 600,
			lineHeight: 1.25,
			textAlign: "center",
			cursor: disabled ? "default" : "pointer",
			opacity: disabled ? 0.45 : 1,
			"& .tool-icon": { transition: "transform .15s ease, box-shadow .15s ease, background .15s ease, color .15s ease" },
			"&:hover:not(:disabled) .tool-icon": { transform: "translateY(-2px)", background: DS.gradient, color: "#FFFFFF", boxShadow: "0 10px 20px -8px rgba(30, 58, 95, 0.6)" },
			"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" }
		}}
	>
		<Box aria-hidden className='tool-icon' sx={{ width: "48px", height: "48px", borderRadius: "50%", background: DS.accentSoft, color: DS.accent, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { width: "20px", height: "20px" } }}>{icon}</Box>
		{title}
	</Box>
)

/** One small figure on a stage, e.g. a limit. */
export const StageStat = ({ label, children }: { label: string; children: ReactNode }) => (
	<VBox gap='3px' sx={{ minWidth: 0, padding: "10px 12px", borderRadius: "12px", background: "rgba(255,255,255,0.75)", border: `1px solid ${DS.line}` }}>
		<Typo sx={{ fontSize: "11px", fontWeight: 500, color: DS.muted, lineHeight: 1.3 }}>{label}</Typo>
		<Box sx={{ fontSize: "15px", fontWeight: 700, color: DS.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{children}</Box>
	</VBox>
)

export const CONTROL_TONES = {
	warning: { background: "#FFFBEB", color: "#B45309", border: "#FDE68A", hover: "#FEF3C7" },
	danger: { background: "#FFFFFF", color: "#B91C1C", border: "#FECACA", hover: "#FEF2F2" },
	success: { background: "#ECFDF5", color: "#047857", border: "#A7F3D0", hover: "#DFF8ED" }
}

export const ControlButton = ({ tone, children, onClick, disabled }: { tone: keyof typeof CONTROL_TONES; children: ReactNode; onClick: () => void; disabled?: boolean }) => {
	const colors = CONTROL_TONES[tone]
	return (
		<Box
			element='button'
			type='button'
			disabled={disabled}
			onClick={disabled ? undefined : onClick}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				gap: "8px",
				minHeight: "44px",
				padding: "0 14px",
				borderRadius: "12px",
				border: `1px solid ${colors.border}`,
				background: colors.background,
				color: colors.color,
				fontFamily: "inherit",
				fontSize: "14px",
				fontWeight: 600,
				cursor: disabled ? "default" : "pointer",
				opacity: disabled ? 0.55 : 1,
				"&:hover:not(:disabled)": { background: colors.hover },
				"&:focus-visible": { outline: `2px solid ${colors.color}`, outlineOffset: "2px" }
			}}
		>
			{children}
		</Box>
	)
}

export const SETTING_TONES = {
	warning: { background: "#FFF7ED", color: "#C2410C" },
	success: { background: "#ECFDF5", color: "#047857" },
	danger: { background: "#FEF2F2", color: "#B91C1C" }
}

/** A settings-style row: icon, what the action does, and the action itself on the right. */
export const SettingRow = ({ tone, title, description, icon, children }: { tone: keyof typeof SETTING_TONES; title: string; description: string; icon: ReactNode; children: ReactNode }) => (
	<HBox gap='14px' sx={{ alignItems: "center", padding: "14px 16px", background: DS.surface, [DS.mobile]: { flexWrap: "wrap" } }}>
		<Box aria-hidden sx={{ width: "40px", height: "40px", minWidth: "40px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", ...SETTING_TONES[tone] }}>{icon}</Box>
		<VBox gap='2px' sx={{ flex: 1, minWidth: "160px" }}>
			<Typo sx={{ fontSize: "14px", fontWeight: 650, color: DS.ink }}>{title}</Typo>
			<Typo sx={{ fontSize: "13px", color: DS.muted, lineHeight: 1.45 }}>{description}</Typo>
		</VBox>
		<Box sx={{ flexShrink: 0, "& > button": { minWidth: "140px", width: "100%" }, [DS.mobile]: { width: "100%" } }}>{children}</Box>
	</HBox>
)
